import React, { useEffect, useState, useMemo, useRef } from 'react';
import './seace-vigentes17.scss';
import {
  FiSearch, FiX, FiSliders, FiRefreshCw, FiBookmark, FiCalendar,
  FiCheck, FiChevronLeft, FiChevronRight, FiCopy, FiExternalLink, FiFileText,
  FiClock, FiHome, FiMapPin, FiList, FiInbox, FiAlertCircle, FiCornerDownRight,
  FiFilePlus, FiTrash2
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import AnexosConvocatoria from '../components/AnexosConvocatoria';
import BotonDocumentos from '../components/BotonDocumentos';
import { CalendarCheck, CalendarX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { enlaceDeBases, buildConvocatoriaMessage, whatsappShareUrl } from '../services/convocatoriaShare';

// Cache global para almacenar los datos
let globalSEACECache = {
  data: null,
  totalRecords: 0,
  timestamp: null,
  isLoading: false
};

const CACHE_DURATION = 60 * 60 * 1000; // 60 minutos en milisegundos
const BATCH_SIZE = 500;

// Búsquedas guardadas: viven solo en el navegador del usuario
const SAVED_KEY = 'nuvia:seace-vigentes:busquedas';

const SEACE_BUSCADOR_URL = 'https://prod2.seace.gob.pe/seacebus-uiwd-pub/buscadorPublico/buscadorPublico.xhtml';

const ORDEN_OPCIONES = [
  { id: 'cierre', label: 'Cierre próximo' },
  { id: 'reciente', label: 'Más recientes' },
  { id: 'entidad', label: 'Entidad (A–Z)' }
];

// Estado inicial de todos los filtros del buscador (17a)
const FILTROS_VACIOS = {
  busqueda: '',
  cierre: 'todos',       // 'todos' | '7' | '15' | 'rango'
  cierreDesde: '',
  cierreHasta: '',
  objetos: [],
  departamentos: [],
  procedimientos: [],
  entidad: '',
  provincia: '',
  nomenclatura: '',
  revision: 'todos'      // 'todos' | 'pendientes' | 'revisados'
};

// ---------------------------------------------------------------
// Utilidades de fecha (las fechas de Supabase vienen en hora Lima)
// ---------------------------------------------------------------
const toDate = (valor) => {
  if (!valor) return null;
  let d = String(valor).replace(' ', 'T');
  if (d.endsWith('+00:00') || /[Zz]$/.test(d)) {
    d = d.replace(/\+00:00$/, '').replace(/[Zz]$/, '') + '-05:00';
  } else if (!/[+-]\d{2}:?\d{2}$/.test(d)) {
    d = d + '-05:00';
  }
  const date = new Date(d);
  return isNaN(date.getTime()) ? null : date;
};

const p2 = (n) => String(n).padStart(2, '0');

// "DD/MM/AAAA"
const fechaCorta = (valor) => {
  const d = toDate(valor);
  if (!d) return '—';
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// "HH:mm"
const soloHora = (valor) => {
  const d = toDate(valor);
  if (!d) return '';
  return `${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

// "DD/MM/AAAA · HH:mm"
const fechaHora = (valor) => {
  const d = toDate(valor);
  if (!d) return '—';
  return `${fechaCorta(valor)} · ${soloHora(valor)}`;
};

// Días completos que faltan hasta una fecha (negativo si ya pasó)
const diasHasta = (valor) => {
  const d = toDate(valor);
  if (!d) return null;
  const fin = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return Math.ceil((fin.getTime() - Date.now()) / 86400000);
};

// Estado de cierre de una convocatoria: texto, color y nivel de urgencia
const cierreInfo = (proceso) => {
  const fecha = proceso.fecha_fin_participantes;
  const dias = diasHasta(fecha);
  if (dias === null) return { dias: null, nivel: 'none', texto: 'Sin fecha', fecha: '' };
  const hora = soloHora(fecha);
  if (dias < 0) return { dias, nivel: 'none', texto: 'Cerrada', fecha: fechaHora(fecha) };
  if (dias === 0) return { dias, nivel: 'urg', texto: `Hoy${hora ? ` · ${hora}` : ''}`, fecha: fechaCorta(fecha) };
  if (dias === 1) return { dias, nivel: 'urg', texto: `Mañana${hora ? ` · ${hora}` : ''}`, fecha: fechaCorta(fecha) };
  if (dias <= 6) return { dias, nivel: 'urg', texto: `En ${dias} días`, fecha: fechaHora(fecha) };
  if (dias <= 30) return { dias, nivel: 'mid', texto: `En ${dias} días`, fecha: fechaHora(fecha) };
  return { dias, nivel: 'far', texto: `En ${dias} días`, fecha: fechaHora(fecha) };
};

// Paleta del badge de objeto contractual
const objetoColor = (objeto) => {
  const o = (objeto || '').toLowerCase();
  if (o.includes('consult')) return { color: '#14507d', bg: '#eff6fb', border: '#d3e3ef' };
  if (o.includes('obra')) return { color: '#7a4a10', bg: '#fdf3e6', border: '#eddcbf' };
  if (o.includes('servici')) return { color: '#0f4f47', bg: '#e8f2f0', border: '#cfe6dd' };
  if (o.includes('bien')) return { color: '#5b3fa8', bg: '#f3effe', border: '#e2d5fb' };
  return { color: '#5b6779', bg: '#f1f4f8', border: '#e2e6ee' };
};

// Capítulo del RNP que corresponde al objeto contractual
const capituloRNP = (objeto) => {
  const o = (objeto || '').toLowerCase();
  if (o.includes('consult')) return 'Consultores de obra';
  if (o.includes('obra')) return 'Ejecutores de obra';
  if (o.includes('servici')) return 'Proveedores de servicios';
  if (o.includes('bien')) return 'Proveedores de bienes';
  return null;
};

const escapeRegExp = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Términos de búsqueda de 3+ letras, para resaltar y para filtrar
const terminosDe = (texto) =>
  (texto || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length >= 3);

// Predicados de filtrado: se construyen desde un estado de filtros, para poder
// reutilizarlos con las búsquedas guardadas y con los conteos de cada faceta.
const construirPredicados = (f, esRevisado) => {
  const terminos = terminosDe(f.busqueda);
  const objetos = (f.objetos || []).map((v) => v.toLowerCase());
  const departamentos = (f.departamentos || []).map((v) => v.toLowerCase());
  const procedimientos = (f.procedimientos || []).map((v) => v.toLowerCase());
  const entidad = (f.entidad || '').trim().toLowerCase();
  const provincia = (f.provincia || '').trim().toLowerCase();
  const nomenclatura = (f.nomenclatura || '').trim().toLowerCase();
  const desde = f.cierreDesde ? new Date(`${f.cierreDesde}T00:00:00-05:00`) : null;
  const hasta = f.cierreHasta ? new Date(`${f.cierreHasta}T23:59:59-05:00`) : null;

  return {
    texto: (i) => {
      if (terminos.length === 0) return true;
      const heno = `${i.descripcion_proceso || ''} ${i.entidad || ''} ${i.nomenclatura || ''}`.toLowerCase();
      return terminos.every((t) => heno.includes(t));
    },
    cierre: (i) => {
      if (f.cierre === 'todos') return true;
      if (f.cierre === 'rango') {
        const d = toDate(i.fecha_fin_participantes);
        if (!d) return false;
        if (desde && d < desde) return false;
        if (hasta && d > hasta) return false;
        return true;
      }
      const dias = diasHasta(i.fecha_fin_participantes);
      if (dias === null || dias < 0) return false;
      return dias <= Number(f.cierre);
    },
    objeto: (i) => objetos.length === 0 || objetos.includes((i.objet || '').toLowerCase()),
    departamento: (i) =>
      departamentos.length === 0 || departamentos.includes((i.departamento || '').toLowerCase()),
    procedimiento: (i) =>
      procedimientos.length === 0 || procedimientos.includes((i.tipo_proceso || '').toLowerCase()),
    avanzada: (i) => {
      if (entidad && !(i.entidad || '').toLowerCase().includes(entidad)) return false;
      if (provincia && (i.provincia || '').toLowerCase() !== provincia) return false;
      if (nomenclatura && !(i.nomenclatura || '').toLowerCase().includes(nomenclatura)) return false;
      if (f.revision === 'pendientes' && esRevisado(i)) return false;
      if (f.revision === 'revisados' && !esRevisado(i)) return false;
      return true;
    }
  };
};

const aplicarPredicados = (lista, predicados, omitir) =>
  lista.filter((i) =>
    Object.keys(predicados).every((k) => k === omitir || predicados[k](i))
  );

const SEACE = ({ user }) => {
  const navigate = useNavigate();

  // Estados principales
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [actualizado, setActualizado] = useState(null);

  // Sistema de revisión
  const [processosRevisados, setProcessosRevisados] = useState(new Set());
  const [loadingReviewed, setLoadingReviewed] = useState(false);

  // Filtros del buscador (17a)
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [mostrarAvanzada, setMostrarAvanzada] = useState(false);
  const [buscarDepto, setBuscarDepto] = useState('');
  const [verTodosDeptos, setVerTodosDeptos] = useState(false);
  const [orden, setOrden] = useState('cierre');

  // Búsquedas guardadas (localStorage)
  const [busquedasGuardadas, setBusquedasGuardadas] = useState([]);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Modal de detalle (17b)
  const [panelIndex, setPanelIndex] = useState(null);
  const [modalTab, setModalTab] = useState('resumen');
  const [copiado, setCopiado] = useState(null);

  // UI
  const [isMobile, setIsMobile] = useState(false);
  const [toast, setToast] = useState(null);
  const [guardandoCalendario, setGuardandoCalendario] = useState(false);
  // Códigos que el usuario ya guardó, para marcar el icono de cada fila.
  // Una sola lectura por usuario; al añadir se suma en local.
  const [codigosEnCalendario, setCodigosEnCalendario] = useState(() => new Set());

  const hasInitialized = useRef(false);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const setFiltro = (parche) => {
    setFiltros((prev) => ({ ...prev, ...parche }));
    setCurrentPage(1);
    setPanelIndex(null);
  };

  // Detectar móvil
  useEffect(() => {
    const checkDevice = () => setIsMobile(window.innerWidth <= 900);
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Cargar búsquedas guardadas del navegador
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(SAVED_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setBusquedasGuardadas(parsed);
      }
    } catch (e) {
      console.warn('No se pudieron leer las búsquedas guardadas:', e);
    }
  }, []);

  const persistirBusquedas = (lista) => {
    setBusquedasGuardadas(lista);
    try {
      window.localStorage.setItem(SAVED_KEY, JSON.stringify(lista));
    } catch (e) {
      console.warn('No se pudieron guardar las búsquedas:', e);
    }
  };

  // ---------------------------------------------------------------
  // Convocatorias revisadas (Supabase)
  // ---------------------------------------------------------------
  const loadUserReviewedProcesses = async (usuario) => {
    const userIdentifier = usuario?.email || usuario?.id || usuario?.user_metadata?.email;
    if (!userIdentifier) return;

    try {
      setLoadingReviewed(true);
      const { data: filas, error: err } = await supabase
        .from('user_reviewed_processes')
        .select('process_id')
        .eq('user_id', userIdentifier);

      if (err) throw err;
      setProcessosRevisados(new Set(filas.map((item) => item.process_id)));
    } catch (e) {
      console.error('Error cargando procesos revisados:', e);
    } finally {
      setLoadingReviewed(false);
    }
  };

  const idProceso = (process) => `${process.nomenclatura}-${process.descripcion_proceso}`;

  const isProcessReviewed = (process) => processosRevisados.has(idProceso(process));

  const toggleProcessReviewed = async (process) => {
    const userIdentifier = user?.email || user?.id || user?.uid;
    if (!userIdentifier) {
      showToast('Usuario no identificado. Verifica tu sesión.', 'warning');
      return;
    }

    const processId = idProceso(process);
    const estabaRevisado = processosRevisados.has(processId);

    try {
      if (estabaRevisado) {
        const { error: err } = await supabase
          .from('user_reviewed_processes')
          .delete()
          .eq('user_id', userIdentifier)
          .eq('process_id', processId);
        if (err) throw err;

        setProcessosRevisados((prev) => {
          const next = new Set(prev);
          next.delete(processId);
          return next;
        });
        showToast('Se quitó de revisadas', 'info');
      } else {
        const { error: err } = await supabase.from('user_reviewed_processes').insert({
          user_id: userIdentifier,
          process_id: processId,
          nomenclatura: process.nomenclatura
        });
        if (err) throw err;

        setProcessosRevisados((prev) => new Set([...prev, processId]));
        showToast('Marcada como revisada', 'success');
      }
    } catch (e) {
      console.error('Error al actualizar estado de revisión:', e);
      showToast(`Error al actualizar: ${e.message}`, 'error');
    }
  };

  // ---------------------------------------------------------------
  // Carga de datos
  // ---------------------------------------------------------------
  const isCacheValid = () => {
    if (!globalSEACECache.data || !globalSEACECache.timestamp) return false;
    return Date.now() - globalSEACECache.timestamp < CACHE_DURATION;
  };

  const getTotalCount = async () => {
    try {
      const { count, error: err } = await supabase
        .from('seace')
        .select('*', { count: 'exact', head: true });
      if (err) throw err;
      return count || 0;
    } catch (e) {
      console.error('Error al obtener conteo total:', e);
      return 0;
    }
  };

  const loadAllDataInBatches = async (totalCount) => {
    const allData = [];
    const batches = Math.ceil(totalCount / BATCH_SIZE);
    setTotalBatches(batches);

    for (let i = 0; i < batches; i++) {
      const start = i * BATCH_SIZE;
      const end = Math.min(start + BATCH_SIZE - 1, totalCount - 1);
      setCurrentBatch(i + 1);

      const { data: batchData, error: err } = await supabase
        .from('seace')
        .select('*')
        .range(start, end);

      if (err) throw err;
      if (batchData && batchData.length > 0) allData.push(...batchData);

      setLoadingProgress(Math.round(((i + 1) / batches) * 100));
      if (i < batches - 1) await new Promise((resolve) => setTimeout(resolve, 50));
    }

    return allData;
  };

  const loadData = async (forceRefresh = false) => {
    if (globalSEACECache.isLoading && !forceRefresh) return;

    setLoading(true);
    setRefreshing(true);
    setError(null);
    setLoadingProgress(0);
    setCurrentBatch(0);
    setTotalBatches(0);
    globalSEACECache.isLoading = true;

    try {
      const totalCount = await getTotalCount();

      if (totalCount === 0) {
        setData([]);
        setTotalRecords(0);
        setDataLoaded(true);
        return;
      }

      const allData = await loadAllDataInBatches(totalCount);

      globalSEACECache.data = allData;
      globalSEACECache.totalRecords = allData.length;
      globalSEACECache.timestamp = Date.now();

      setData(allData);
      setTotalRecords(allData.length);
      setActualizado(globalSEACECache.timestamp);
      setDataLoaded(true);
    } catch (e) {
      console.error('Error al cargar datos SEACE:', e);
      setError(`Error al cargar los datos: ${e.message || 'Error desconocido'}. Por favor, intenta nuevamente.`);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingProgress(100);
      setCurrentBatch(0);
      setTotalBatches(0);
      globalSEACECache.isLoading = false;
    }
  };

  const loadDataIfNeeded = async () => {
    if (isCacheValid() && !globalSEACECache.isLoading) {
      setData(globalSEACECache.data);
      setTotalRecords(globalSEACECache.totalRecords);
      setActualizado(globalSEACECache.timestamp);
      setDataLoaded(true);
      return;
    }

    if (globalSEACECache.isLoading) {
      if (globalSEACECache.data) {
        setData(globalSEACECache.data);
        setTotalRecords(globalSEACECache.totalRecords);
        setActualizado(globalSEACECache.timestamp);
        setDataLoaded(true);
      }
      return;
    }

    await loadData();
  };

  const refreshData = () => {
    globalSEACECache.data = null;
    globalSEACECache.timestamp = null;
    loadData(true);
  };

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      loadDataIfNeeded();
    }
    if (user?.email) loadUserReviewedProcesses(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ---------------------------------------------------------------
  // Agrupación por procedimiento
  // ---------------------------------------------------------------
  const dataAgrupada = useMemo(() => {
    const grupos = new Map();

    data.forEach((item) => {
      const clave = `${item.id_procedimiento}`;

      if (!grupos.has(clave)) {
        grupos.set(clave, {
          created_at: item.created_at,
          entidad: item.entidad,
          tipo_proceso: item.tipo_proceso,
          num_proceso: item.num_proceso,
          nomenclatura: item.nomenclatura,
          departamento: item.departamento,
          provincia: item.provincia,
          distrito: item.distrito,
          objet: item.objeto || item.objet,
          moneda: item.moneda,
          fecha_convocatoria: item.fecha_convocatoria,
          fecha_presentacion_propuestas: item.fecha_presentacion_propuestas,
          descripcion_proceso: item.descripcion_proceso,
          fecha_inicio_participantes: item.fecha_inicio_participantes,
          fecha_fin_participantes: item.fecha_fin_participantes,
          url_doc: item.url_doc,
          url_anexos: item.url_anexos,
          items: [],
          total_items: 0,
          detalle_items_concat: ''
        });
      }

      const grupo = grupos.get(clave);

      if (item.detalle_item) {
        grupo.items.push({
          id_procedimiento: item.id_procedimiento,
          detalle_item: item.detalle_item,
          created_at: item.created_at,
          cantItem: item.cantItem,
          detCubso: item.detCubso,
          detUnidadMedida: item.detUnidadMedida
        });
      }

      grupo.total_items = grupo.items.length;
      grupo.detalle_items_concat = grupo.items
        .map((it) => it.detalle_item)
        .filter(Boolean)
        .join('; ');
    });

    return Array.from(grupos.values());
  }, [data]);

  // Valores únicos para las facetas
  const objetosUnicos = useMemo(
    () => Array.from(new Set(dataAgrupada.map((i) => i.objet).filter(Boolean))).sort(),
    [dataAgrupada]
  );
  const departamentosUnicos = useMemo(
    () => Array.from(new Set(dataAgrupada.map((i) => i.departamento).filter(Boolean))).sort(),
    [dataAgrupada]
  );
  const procedimientosUnicos = useMemo(
    () => Array.from(new Set(dataAgrupada.map((i) => i.tipo_proceso).filter(Boolean))).sort(),
    [dataAgrupada]
  );
  const provinciasUnicas = useMemo(() => {
    const deptos = filtros.departamentos.map((d) => d.toLowerCase());
    const base = deptos.length
      ? dataAgrupada.filter((i) => deptos.includes((i.departamento || '').toLowerCase()))
      : dataAgrupada;
    return Array.from(new Set(base.map((i) => i.provincia).filter(Boolean))).sort();
  }, [dataAgrupada, filtros.departamentos]);

  // ---------------------------------------------------------------
  // Filtrado con facetas
  // ---------------------------------------------------------------
  const predicados = useMemo(
    () => construirPredicados(filtros, isProcessReviewed),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtros, processosRevisados]
  );

  const dataFiltrada = useMemo(() => {
    const lista = aplicarPredicados(dataAgrupada, predicados, null);

    const porFecha = (v) => {
      const d = toDate(v);
      return d ? d.getTime() : null;
    };

    lista.sort((a, b) => {
      if (orden === 'entidad') return (a.entidad || '').localeCompare(b.entidad || '', 'es');
      if (orden === 'reciente') {
        const av = porFecha(a.fecha_convocatoria);
        const bv = porFecha(b.fecha_convocatoria);
        if (av === null) return 1;
        if (bv === null) return -1;
        return bv - av;
      }
      const av = porFecha(a.fecha_fin_participantes);
      const bv = porFecha(b.fecha_fin_participantes);
      if (av === null) return 1;
      if (bv === null) return -1;
      return av - bv;
    });

    return lista;
  }, [dataAgrupada, predicados, orden]);

  // Conteos en vivo de cada faceta (calculados sin aplicarse a sí misma)
  const conteos = useMemo(() => {
    const contarPor = (lista, campo) => {
      const mapa = new Map();
      lista.forEach((i) => {
        const v = i[campo];
        if (!v) return;
        mapa.set(v, (mapa.get(v) || 0) + 1);
      });
      return mapa;
    };

    const baseCierre = aplicarPredicados(dataAgrupada, predicados, 'cierre');
    const cierre = { todos: baseCierre.length, 7: 0, 15: 0 };
    baseCierre.forEach((i) => {
      const d = diasHasta(i.fecha_fin_participantes);
      if (d === null || d < 0) return;
      if (d <= 7) cierre[7] += 1;
      if (d <= 15) cierre[15] += 1;
    });

    return {
      cierre,
      objeto: contarPor(aplicarPredicados(dataAgrupada, predicados, 'objeto'), 'objet'),
      departamento: contarPor(aplicarPredicados(dataAgrupada, predicados, 'departamento'), 'departamento'),
      procedimiento: contarPor(aplicarPredicados(dataAgrupada, predicados, 'procedimiento'), 'tipo_proceso')
    };
  }, [dataAgrupada, predicados]);

  const totalPages = Math.max(1, Math.ceil(dataFiltrada.length / itemsPerPage));

  const dataPaginada = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return dataFiltrada.slice(start, start + itemsPerPage);
  }, [dataFiltrada, currentPage, itemsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  // ---------------------------------------------------------------
  // Acciones sobre los filtros
  // ---------------------------------------------------------------
  const alternarEnLista = (clave, valor) => {
    setFiltros((prev) => {
      const actual = prev[clave];
      return {
        ...prev,
        [clave]: actual.includes(valor) ? actual.filter((v) => v !== valor) : [...actual, valor]
      };
    });
    setCurrentPage(1);
    setPanelIndex(null);
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_VACIOS);
    setCurrentPage(1);
    setPanelIndex(null);
    setBuscarDepto('');
  };

  const filtrosActivos = useMemo(() => {
    const chips = [];
    if (filtros.cierre === '7') chips.push({ key: 'cierre', label: 'Próximos 7 días', clear: () => setFiltro({ cierre: 'todos' }) });
    if (filtros.cierre === '15') chips.push({ key: 'cierre', label: 'Próximos 15 días', clear: () => setFiltro({ cierre: 'todos' }) });
    if (filtros.cierre === 'rango') {
      chips.push({
        key: 'cierre',
        label: `Cierre ${filtros.cierreDesde || '…'} → ${filtros.cierreHasta || '…'}`,
        clear: () => setFiltro({ cierre: 'todos', cierreDesde: '', cierreHasta: '' })
      });
    }
    filtros.objetos.forEach((o) =>
      chips.push({ key: `obj-${o}`, label: o, clear: () => alternarEnLista('objetos', o) })
    );
    filtros.departamentos.forEach((d) =>
      chips.push({ key: `dep-${d}`, label: d, clear: () => alternarEnLista('departamentos', d) })
    );
    filtros.procedimientos.forEach((p) =>
      chips.push({ key: `proc-${p}`, label: p, clear: () => alternarEnLista('procedimientos', p) })
    );
    if (filtros.entidad) chips.push({ key: 'ent', label: `Entidad: ${filtros.entidad}`, clear: () => setFiltro({ entidad: '' }) });
    if (filtros.provincia) chips.push({ key: 'prov', label: filtros.provincia, clear: () => setFiltro({ provincia: '' }) });
    if (filtros.nomenclatura) chips.push({ key: 'nom', label: `Nom: ${filtros.nomenclatura}`, clear: () => setFiltro({ nomenclatura: '' }) });
    if (filtros.revision !== 'todos') {
      chips.push({
        key: 'rev',
        label: filtros.revision === 'pendientes' ? 'Pendientes' : 'Revisadas',
        clear: () => setFiltro({ revision: 'todos' })
      });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros]);

  const hayFiltros = filtrosActivos.length > 0 || filtros.busqueda.trim() !== '';

  // ---------------------------------------------------------------
  // Búsquedas guardadas
  // ---------------------------------------------------------------
  const conteosGuardadas = useMemo(() => {
    const mapa = {};
    busquedasGuardadas.forEach((b) => {
      const preds = construirPredicados({ ...FILTROS_VACIOS, ...b.filtros }, isProcessReviewed);
      mapa[b.id] = aplicarPredicados(dataAgrupada, preds, null).length;
    });
    return mapa;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busquedasGuardadas, dataAgrupada, processosRevisados]);

  const firmaFiltros = (f) => JSON.stringify({ ...FILTROS_VACIOS, ...f });
  const firmaActual = firmaFiltros(filtros);

  const guardarBusqueda = () => {
    if (!hayFiltros) {
      showToast('Aplica algún filtro antes de guardar la búsqueda', 'warning');
      return;
    }
    const sugerido = filtros.busqueda.trim() || filtrosActivos.map((c) => c.label).slice(0, 2).join(' · ');
    const nombre = window.prompt('Nombre de la búsqueda:', sugerido.slice(0, 40));
    if (!nombre || !nombre.trim()) return;

    const nueva = { id: `b-${Date.now()}`, nombre: nombre.trim(), filtros: { ...filtros } };
    persistirBusquedas([...busquedasGuardadas.filter((b) => b.nombre !== nueva.nombre), nueva]);
    showToast('Búsqueda guardada en este navegador', 'success');
  };

  const aplicarBusquedaGuardada = (b) => {
    setFiltros({ ...FILTROS_VACIOS, ...b.filtros });
    setCurrentPage(1);
    setPanelIndex(null);
  };

  const borrarBusqueda = (id, e) => {
    e.stopPropagation();
    persistirBusquedas(busquedasGuardadas.filter((b) => b.id !== id));
  };

  // ---------------------------------------------------------------
  // Calendario y utilidades del modal
  // ---------------------------------------------------------------
  const usuarioCalendario = user?.email || user?.id || user?.user_metadata?.email;

  useEffect(() => {
    if (!usuarioCalendario) { setCodigosEnCalendario(new Set()); return; }
    let vigente = true;
    (async () => {
      try {
        const { data: filas, error: err } = await supabase
          .from('calendar_events')
          .select('nomenclatura')
          .eq('user_id', usuarioCalendario)
          .eq('fuente', 'SEACE');
        if (err) throw err;
        if (vigente) setCodigosEnCalendario(new Set((filas || []).map((f) => f.nomenclatura).filter(Boolean)));
      } catch (e) {
        console.error('Error leyendo el calendario del usuario:', e);
      }
    })();
    return () => { vigente = false; };
  }, [usuarioCalendario]);

  const agregarAlCalendario = async (process) => {
    const userIdentifier = user?.email || user?.id || user?.user_metadata?.email;
    if (!userIdentifier) {
      showToast('Usuario no identificado', 'warning');
      return;
    }

    setGuardandoCalendario(true);
    try {
      const { data: existentes } = await supabase
        .from('calendar_events')
        .select('id')
        .eq('user_id', userIdentifier)
        .eq('nomenclatura', process.nomenclatura)
        .eq('fuente', 'SEACE')
        .limit(1);

      if (existentes && existentes.length > 0) {
        showToast('Esta convocatoria ya está en el calendario', 'info');
        return;
      }

      const eventos = [];
      const push = (tipo, fecha) => {
        if (fecha) eventos.push({
          user_id: userIdentifier,
          nomenclatura: process.nomenclatura,
          entidad: process.entidad,
          descripcion_proceso: process.descripcion_proceso,
          tipo_evento: tipo,
          fecha_evento: fecha,
          fuente: 'SEACE',
          departamento: process.departamento,
          objeto: process.objet,
          created_at: new Date().toISOString()
        });
      };
      push('convocatoria', process.fecha_convocatoria);
      push('inicio_registro', process.fecha_inicio_participantes);
      push('fin_registro', process.fecha_fin_participantes);
      push('presentacion_propuestas', process.fecha_presentacion_propuestas);

      if (eventos.length === 0) {
        showToast('No hay fechas disponibles para agregar', 'warning');
        return;
      }

      const { error: err } = await supabase.from('calendar_events').insert(eventos);
      if (err) throw err;
      setCodigosEnCalendario((prev) => new Set(prev).add(process.nomenclatura));
      showToast(`Se agregaron ${eventos.length} eventos al calendario`, 'success');
    } catch (e) {
      console.error('Error agregando al calendario:', e);
      showToast(`Error al agregar al calendario: ${e.message}`, 'error');
    } finally {
      setGuardandoCalendario(false);
    }
  };

  // Quitar del calendario: borra los hitos SEACE de esa convocatoria. El mismo
  // icono sirve para poner y quitar, sin pasar por el Calendario de Procesos.
  const quitarDelCalendario = async (process) => {
    const userIdentifier = user?.email || user?.id || user?.user_metadata?.email;
    if (!userIdentifier) {
      showToast('Usuario no identificado', 'warning');
      return;
    }

    setGuardandoCalendario(true);
    try {
      const { error: err } = await supabase
        .from('calendar_events')
        .delete()
        .eq('user_id', userIdentifier)
        .eq('nomenclatura', process.nomenclatura)
        .eq('fuente', 'SEACE');
      if (err) throw err;
      setCodigosEnCalendario((prev) => {
        const siguiente = new Set(prev);
        siguiente.delete(process.nomenclatura);
        return siguiente;
      });
      showToast('Se quitó del calendario', 'success');
    } catch (e) {
      console.error('Error quitando del calendario:', e);
      showToast(`Error al quitar del calendario: ${e.message}`, 'error');
    } finally {
      setGuardandoCalendario(false);
    }
  };

  // Un clic pone la convocatoria en el calendario; el siguiente la saca.
  const alternarCalendario = (process) => {
    if (!process?.nomenclatura || guardandoCalendario) return;
    return codigosEnCalendario.has(process.nomenclatura)
      ? quitarDelCalendario(process)
      : agregarAlCalendario(process);
  };

  // Marca como revisadas todas las convocatorias del resultado actual
  const marcarResultadosRevisados = async () => {
    const userIdentifier = user?.email || user?.id || user?.user_metadata?.email;
    if (!userIdentifier) {
      showToast('Usuario no identificado', 'warning');
      return;
    }

    const pendientes = dataFiltrada.filter((p) => !isProcessReviewed(p));
    if (pendientes.length === 0) {
      showToast('Todos los resultados ya están revisados', 'info');
      return;
    }
    if (!window.confirm(`¿Marcar ${pendientes.length} convocatorias del resultado actual como revisadas?`)) return;

    try {
      const filas = pendientes.map((p) => ({
        user_id: userIdentifier,
        process_id: idProceso(p),
        nomenclatura: p.nomenclatura
      }));

      for (let i = 0; i < filas.length; i += BATCH_SIZE) {
        const { error: err } = await supabase
          .from('user_reviewed_processes')
          .insert(filas.slice(i, i + BATCH_SIZE));
        if (err) throw err;
      }

      setProcessosRevisados((prev) => new Set([...prev, ...filas.map((f) => f.process_id)]));
      showToast(`Se marcaron ${filas.length} convocatorias como revisadas`, 'success');
    } catch (e) {
      console.error('Error marcando como revisadas:', e);
      showToast(`Error al marcar: ${e.message}`, 'error');
    }
  };

  const abrirBases = (url) => {
    if (url && url.trim() !== '') {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      showToast('No hay documento de bases disponible', 'warning');
    }
  };

  // WhatsApp se abre en el mismo gesto del clic: si se hiciera después de un
  // await, el navegador lo trataría como ventana emergente y la bloquearía.
  const compartirWhatsApp = (proceso) => {
    const mensaje = buildConvocatoriaMessage(
      proceso,
      enlaceDeBases(proceso.url_doc),
      'SEACE · Convocatoria vigente'
    );
    const ventana = window.open(whatsappShareUrl(mensaje), '_blank');
    if (ventana) ventana.opener = null;
    else showToast('Permite las ventanas emergentes para abrir WhatsApp.', 'warning');
  };

  const fichaTexto = (p) => {
    const itemsInfo = (p.items || [])
      .map((it, i) => `${i + 1}. ${it.detalle_item || '—'}${it.detCubso ? ` (CUBSO ${it.detCubso})` : ''}`)
      .join('\n') || 'Sin ítems';

    return [
      'SEACE — Convocatoria vigente',
      `Nomenclatura: ${p.nomenclatura || '-'}`,
      `Entidad: ${p.entidad || '-'}`,
      `Procedimiento: ${p.tipo_proceso || '-'}`,
      `N.º de proceso: ${p.num_proceso || '-'}`,
      `Objeto contractual: ${p.objet || '-'}`,
      `Moneda: ${p.moneda || '-'}`,
      `Ubicación: ${[p.departamento, p.provincia, p.distrito].filter(Boolean).join(' · ') || '-'}`,
      `Total de ítems: ${p.total_items || 0}`,
      `Convocatoria: ${fechaHora(p.fecha_convocatoria)}`,
      `Registro de participantes: ${fechaHora(p.fecha_inicio_participantes)} → ${fechaHora(p.fecha_fin_participantes)}`,
      `Presentación de ofertas: ${fechaHora(p.fecha_presentacion_propuestas)}`,
      `Descripción: ${p.descripcion_proceso || '-'}`,
      `Bases: ${p.url_doc || 'No disponible'}`,
      '',
      'ÍTEMS:',
      itemsInfo
    ].join('\n');
  };

  const copiar = (texto, clave) => {
    navigator.clipboard.writeText(texto).then(
      () => {
        setCopiado(clave);
        setTimeout(() => setCopiado(null), 1600);
      },
      () => showToast('No se pudo copiar al portapapeles', 'error')
    );
  };

  const generarOferta = (proceso) => {
    navigate('/SEACE/generador-oferta', { state: { proceso } });
  };

  // ---------------------------------------------------------------
  // Modal de detalle (17b)
  // ---------------------------------------------------------------
  const procesoPanel =
    panelIndex != null && panelIndex >= 0 && panelIndex < dataFiltrada.length
      ? dataFiltrada[panelIndex]
      : null;

  const abrirPanel = (indexGlobal) => {
    setPanelIndex(indexGlobal);
    setModalTab('resumen');
  };
  const cerrarPanel = () => setPanelIndex(null);

  const panelNav = (delta) => {
    setPanelIndex((i) => {
      const base = i == null ? 0 : i;
      const n = Math.min(dataFiltrada.length - 1, Math.max(0, base + delta));
      setCurrentPage(Math.floor(n / itemsPerPage) + 1);
      return n;
    });
    setModalTab('resumen');
  };

  useEffect(() => {
    const onKey = (e) => {
      if (panelIndex == null) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const k = e.key.toLowerCase();
      if (k === 'j') { e.preventDefault(); panelNav(1); }
      else if (k === 'k') { e.preventDefault(); panelNav(-1); }
      else if (k === 's') { e.preventDefault(); if (procesoPanel) toggleProcessReviewed(procesoPanel); }
      else if (e.key === 'Escape') cerrarPanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelIndex, dataFiltrada.length, itemsPerPage, procesoPanel]);

  // Progreso de la ventana de registro
  const ventanaRegistro = (proceso) => {
    const a = toDate(proceso.fecha_inicio_participantes);
    const b = toDate(proceso.fecha_fin_participantes);
    if (!a || !b || b <= a) return null;
    const totalDias = Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000));
    const transcurrido = Math.round((Date.now() - a.getTime()) / 86400000);
    const diaActual = Math.min(totalDias, Math.max(1, transcurrido + 1));
    const pct = Math.min(100, Math.max(0, ((Date.now() - a.getTime()) / (b.getTime() - a.getTime())) * 100));
    return { diaActual, totalDias, pct };
  };

  const registroEnCurso = (proceso) => {
    const a = toDate(proceso.fecha_inicio_participantes);
    const b = toDate(proceso.fecha_fin_participantes);
    const now = Date.now();
    return !!(a && b && a.getTime() <= now && now <= b.getTime());
  };

  const cuentaRegresiva = (fecha) => {
    const d = toDate(fecha);
    if (!d) return null;
    const ms = d.getTime() - Date.now();
    if (ms <= 0) return { label: 'Registro cerrado', urgente: false };
    const dias = Math.floor(ms / 86400000);
    const horas = Math.floor((ms % 86400000) / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    if (dias >= 1) return { label: `${dias} d ${horas} h`, urgente: dias <= 1 };
    return { label: `${horas} h ${mins} m`, urgente: true };
  };

  const estadoHito = (fecha, activo) => {
    if (activo) return { label: 'abierto', color: '#0f4f47', bg: '#e8f2f0' };
    const dias = diasHasta(fecha);
    if (dias === null) return null;
    if (dias < 0) return { label: 'cumplido', color: '#5b6779', bg: '#eef1f5' };
    if (dias === 0) return { label: 'hoy', color: '#b42318', bg: '#fdeceb' };
    if (dias === 1) return { label: 'mañana', color: '#8a5308', bg: '#fdf3e6' };
    return { label: `en ${dias} días`, color: '#475569', bg: '#eef1f5' };
  };

  const renderCrono = (proceso) => {
    const enCurso = registroEnCurso(proceso);
    const hitos = [
      { label: 'Convocatoria', fecha: proceso.fecha_convocatoria, tipo: 'done' },
      { label: 'Registro de participantes', fecha: proceso.fecha_inicio_participantes, tipo: enCurso ? 'on' : 'done', activo: enCurso },
      { label: 'Cierre del registro', fecha: proceso.fecha_fin_participantes, tipo: 'next' },
      { label: 'Presentación de ofertas', fecha: proceso.fecha_presentacion_propuestas, tipo: 'todo' }
    ].filter((h) => h.fecha);

    const ventana = ventanaRegistro(proceso);

    return (
      <div className="sv17-crono">
        <div className="sv17-crono-grid">
          {hitos.map((h, i) => {
            const est = estadoHito(h.fecha, h.activo);
            const ultimo = i === hitos.length - 1;
            return (
              <React.Fragment key={h.label}>
                <div className={`sv17-crono-rail is-${h.tipo}`}>
                  <i />
                  {!ultimo && <u />}
                </div>
                <div className={`sv17-crono-cell ${ultimo ? 'is-last' : ''}`}>
                  <div className={`sv17-crono-label ${h.activo ? 'is-strong' : ''}`}>
                    <span>{h.label}</span>
                    {est && (
                      <span className="sv17-crono-badge" style={{ color: est.color, background: est.bg }}>
                        {est.label}
                      </span>
                    )}
                  </div>
                  <div className="sv17-crono-date">{fechaHora(h.fecha)}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {ventana && (
          <div className="sv17-crono-window">
            <div className="sv17-crono-window-top">
              <span>Ventana de registro</span>
              <b>día {ventana.diaActual} de {ventana.totalDias}</b>
            </div>
            <div className="sv17-crono-window-bar">
              <span style={{ width: `${Math.max(2, ventana.pct)}%` }} />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Resalta los términos buscados dentro de la descripción
  const resaltar = (texto) => {
    const terminos = terminosDe(filtros.busqueda);
    if (!texto) return '—';
    if (terminos.length === 0) return texto;
    const re = new RegExp(`(${terminos.map(escapeRegExp).join('|')})`, 'gi');
    return String(texto)
      .split(re)
      .map((parte, i) =>
        terminos.includes(parte.toLowerCase()) ? <mark key={i}>{parte}</mark> : <React.Fragment key={i}>{parte}</React.Fragment>
      );
  };

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------
  const deptosVisibles = useMemo(() => {
    const q = buscarDepto.trim().toLowerCase();
    const lista = q ? departamentosUnicos.filter((d) => d.toLowerCase().includes(q)) : departamentosUnicos;
    const ordenados = [...lista].sort(
      (a, b) => (conteos.departamento.get(b) || 0) - (conteos.departamento.get(a) || 0)
    );
    return verTodosDeptos || q ? ordenados : ordenados.slice(0, 5);
  }, [departamentosUnicos, buscarDepto, verTodosDeptos, conteos]);

  const paginas = useMemo(() => {
    const res = [];
    const max = isMobile ? 3 : 5;
    if (totalPages <= max + 2) {
      for (let i = 1; i <= totalPages; i++) res.push(i);
      return res;
    }
    res.push(1);
    const desde = Math.max(2, currentPage - Math.floor(max / 2));
    const hasta = Math.min(totalPages - 1, desde + max - 2);
    if (desde > 2) res.push('…');
    for (let i = desde; i <= hasta; i++) res.push(i);
    if (hasta < totalPages - 1) res.push('…');
    res.push(totalPages);
    return res;
  }, [totalPages, currentPage, isMobile]);

  const horaActualizado = actualizado
    ? new Date(actualizado).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: false })
    : null;

  const cargando = loading && dataAgrupada.length === 0;

  return (
    <div className="sv17">
      {toast && (
        <div className={`sv17-toast is-${toast.type === 'success' ? 'success' : toast.type === 'warning' ? 'warning' : toast.type === 'error' ? 'error' : 'info'}`}>
          {toast.message}
        </div>
      )}

      {/* ── Cabecera ─────────────────────────────────────────── */}
      <div className="sv17-head">
        <div style={{ minWidth: 0 }}>
          <div className="sv17-eyebrow">SEACE · TODO EL PERÚ</div>
          <h1 className="sv17-title">Convocatorias Vigentes</h1>
          <div className="sv17-sub" title={`${totalRecords.toLocaleString('es-PE')} registros descargados del SEACE`}>
            {dataAgrupada.length.toLocaleString('es-PE')} Convocatorias vigentes en este momento
            {horaActualizado && ` · actualizado ${horaActualizado}`}
          </div>
        </div>

        <div className="sv17-head-actions">
          <button className="sv17-btn" onClick={refreshData} disabled={refreshing} hidden style={{ display: 'none' }}>
            <FiRefreshCw className={refreshing ? 'sv17-spin' : ''} />
            {refreshing ? 'Actualizando…' : 'Actualizar'}
          </button>
          <button className="sv17-btn sv17-btn-primary" onClick={guardarBusqueda}>
            <FiBookmark />
            Guardar esta búsqueda
          </button>
        </div>
      </div>

      {/* ── Buscador + búsquedas guardadas ───────────────────── */}
      <div className="sv17-searchcard">
        <div className="sv17-searchrow">
          <div className="sv17-search">
            <FiSearch />
            <input
              type="text"
              value={filtros.busqueda}
              onChange={(e) => setFiltro({ busqueda: e.target.value })}
              placeholder="Buscar por descripción, entidad o nomenclatura…"
            />
            {filtros.busqueda && (
              <button className="sv17-search-clear" onClick={() => setFiltro({ busqueda: '' })} title="Limpiar">
                <FiX />
              </button>
            )}
          </div>
          <button
            className={`sv17-btn ${mostrarAvanzada ? 'sv17-btn-tint' : ''}`}
            onClick={() => setMostrarAvanzada((v) => !v)}
          >
            <FiSliders />
            Búsqueda avanzada
          </button>
        </div>

        <div className="sv17-saved">
          <span className="sv17-saved-label">MIS BÚSQUEDAS</span>
          {busquedasGuardadas.length === 0 && (
            <span className="sv17-saved-empty">
              Guarda una búsqueda para volver a ella con un clic — se queda en este navegador.
            </span>
          )}
          {busquedasGuardadas.map((b) => {
            const activa = firmaFiltros(b.filtros) === firmaActual;
            return (
              <span
                key={b.id}
                className={`sv17-savedchip ${activa ? 'is-on' : ''}`}
                onClick={() => aplicarBusquedaGuardada(b)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && aplicarBusquedaGuardada(b)}
              >
                {b.nombre}
                <b>{(conteosGuardadas[b.id] || 0).toLocaleString('es-PE')}</b>
                <button className="sv17-savedchip-x" onClick={(e) => borrarBusqueda(b.id, e)} title="Eliminar búsqueda">
                  <FiTrash2 />
                </button>
              </span>
            );
          })}
          <span
            className={`sv17-savedchip ${filtros.revision === 'revisados' ? 'is-on' : ''}`}
            onClick={() => setFiltro({ revision: filtros.revision === 'revisados' ? 'todos' : 'revisados' })}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setFiltro({ revision: filtros.revision === 'revisados' ? 'todos' : 'revisados' })}
          >
            <FiCheck />
            Revisadas
          </span>
        </div>

        {mostrarAvanzada && (
          <div className="sv17-adv">
            <div className="sv17-field">
              <label>ENTIDAD CONTIENE</label>
              <input
                type="text"
                value={filtros.entidad}
                onChange={(e) => setFiltro({ entidad: e.target.value })}
                placeholder="Municipalidad, GORE, SEDAPAL…"
              />
            </div>
            <div className="sv17-field">
              <label>NOMENCLATURA CONTIENE</label>
              <input
                type="text"
                value={filtros.nomenclatura}
                onChange={(e) => setFiltro({ nomenclatura: e.target.value })}
                placeholder="CP-ABR-2-2026…"
              />
            </div>
            <div className="sv17-field">
              <label>PROVINCIA</label>
              <select value={filtros.provincia} onChange={(e) => setFiltro({ provincia: e.target.value })}>
                <option value="">Todas las provincias</option>
                {provinciasUnicas.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="sv17-field">
              <label>ESTADO DE REVISIÓN</label>
              <select value={filtros.revision} onChange={(e) => setFiltro({ revision: e.target.value })}>
                <option value="todos">Todas</option>
                <option value="pendientes">Solo pendientes</option>
                <option value="revisados">Solo revisadas</option>
              </select>
            </div>
            <div className="sv17-adv-foot">
              <button className="sv17-btn" onClick={marcarResultadosRevisados} disabled={dataFiltrada.length === 0 || loadingReviewed}>
                <FiCheck />
                Marcar resultados como revisados
              </button>
              <button className="sv17-btn" onClick={limpiarFiltros} disabled={!hayFiltros}>
                <FiX />
                Limpiar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="sv17-alert">
          <FiAlertCircle />
          <span>{error}</span>
        </div>
      )}

      {/* ── Facetas + resultados ─────────────────────────────── */}
      <div className="sv17-grid">
        {/* Panel de facetas */}
        <aside className="sv17-facets">
          <div className="sv17-facets-head">
            <span>FILTROS</span>
            <button className="sv17-link" onClick={limpiarFiltros} disabled={!hayFiltros}>
              {hayFiltros ? `Limpiar (${filtrosActivos.length})` : 'Sin filtros'}
            </button>
          </div>

          {/* Cierre */}
          <div className="sv17-facet">
            <div className="sv17-facet-title">Cierre de la convocatoria</div>
            <div className="sv17-facet-list">
              {[
                { id: 'todos', label: 'Cualquier fecha', count: conteos.cierre.todos },
                { id: '7', label: 'Próximos 7 días', count: conteos.cierre[7] },
                { id: '15', label: 'Próximos 15 días', count: conteos.cierre[15] },
                { id: 'rango', label: 'Rango personalizado', count: null }
              ].map((o) => (
                <button
                  key={o.id}
                  className={`sv17-opt ${filtros.cierre === o.id ? 'is-on' : ''}`}
                  onClick={() => setFiltro({ cierre: o.id })}
                >
                  <span className="sv17-radio"><span /></span>
                  <span className="sv17-opt-txt">{o.label}</span>
                  {o.count != null && <span className="sv17-opt-count">{o.count.toLocaleString('es-PE')}</span>}
                </button>
              ))}
            </div>
            {filtros.cierre === 'rango' && (
              <div className="sv17-range">
                <input type="date" value={filtros.cierreDesde} onChange={(e) => setFiltro({ cierreDesde: e.target.value })} />
                <span>→</span>
                <input type="date" value={filtros.cierreHasta} onChange={(e) => setFiltro({ cierreHasta: e.target.value })} />
              </div>
            )}
          </div>

          {/* Objeto contractual */}
          <div className="sv17-facet">
            <div className="sv17-facet-title">Objeto contractual</div>
            <div className="sv17-facet-list">
              {objetosUnicos.length === 0 && <span className="sv17-saved-empty">Sin datos</span>}
              {objetosUnicos.map((o) => {
                const on = filtros.objetos.includes(o);
                return (
                  <button key={o} className={`sv17-opt ${on ? 'is-on' : ''}`} onClick={() => alternarEnLista('objetos', o)}>
                    <span className="sv17-check"><FiCheck /></span>
                    <span className="sv17-opt-txt" title={o}>{o}</span>
                    <span className="sv17-opt-count">{(conteos.objeto.get(o) || 0).toLocaleString('es-PE')}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Departamento */}
          <div className="sv17-facet">
            <div className="sv17-facet-title">
              <span>Departamento</span>
              {filtros.departamentos.length > 0 && (
                <button className="sv17-link" onClick={() => setFiltro({ departamentos: [] })}>Quitar</button>
              )}
            </div>
            <div className="sv17-facet-search">
              <FiSearch />
              <input
                type="text"
                value={buscarDepto}
                onChange={(e) => setBuscarDepto(e.target.value)}
                placeholder="Filtrar departamentos"
              />
            </div>
            <div className="sv17-facet-list">
              {deptosVisibles.map((d) => {
                const on = filtros.departamentos.includes(d);
                return (
                  <button key={d} className={`sv17-opt ${on ? 'is-on' : ''}`} onClick={() => alternarEnLista('departamentos', d)}>
                    <span className="sv17-check"><FiCheck /></span>
                    <span className="sv17-opt-txt" title={d}>{d}</span>
                    <span className="sv17-opt-count">{(conteos.departamento.get(d) || 0).toLocaleString('es-PE')}</span>
                  </button>
                );
              })}
            </div>
            {!buscarDepto && departamentosUnicos.length > 5 && (
              <button className="sv17-link sv17-facet-more" onClick={() => setVerTodosDeptos((v) => !v)}>
                {verTodosDeptos ? 'Ver menos' : `Ver los ${departamentosUnicos.length} departamentos`}
              </button>
            )}
          </div>

          {/* Procedimiento */}
          <div className="sv17-facet">
            <div className="sv17-facet-title">Tipo de procedimiento</div>
            <div className="sv17-facet-list">
              {procedimientosUnicos.length === 0 && <span className="sv17-saved-empty">Sin datos</span>}
              {procedimientosUnicos.map((p) => {
                const on = filtros.procedimientos.includes(p);
                return (
                  <button key={p} className={`sv17-opt ${on ? 'is-on' : ''}`} onClick={() => alternarEnLista('procedimientos', p)}>
                    <span className="sv17-check"><FiCheck /></span>
                    <span className="sv17-opt-txt" title={p}>{p}</span>
                    <span className="sv17-opt-count">{(conteos.procedimiento.get(p) || 0).toLocaleString('es-PE')}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Resultados */}
        <div>
          <div className="sv17-resbar">
            <span className="sv17-rescount">
              <b>{dataFiltrada.length.toLocaleString('es-PE')} convocatorias</b> vigentes con estos filtros
            </span>
            {filtrosActivos.map((c) => (
              <span className="sv17-chip" key={c.key}>
                <span title={c.label}>{c.label}</span>
                <button onClick={c.clear} title="Quitar filtro"><FiX /></button>
              </span>
            ))}
            <span className="sv17-spacer" />
            <div className="sv17-seg">
              {ORDEN_OPCIONES.map((o) => (
                <button
                  key={o.id}
                  className={orden === o.id ? 'is-on' : ''}
                  onClick={() => { setOrden(o.id); setCurrentPage(1); setPanelIndex(null); }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {cargando ? (
            <div className="sv17-panel">
              <div className="sv17-panel-ic"><FiRefreshCw className="sv17-spin" /></div>
              <div className="sv17-panel-title">Cargando convocatorias del SEACE</div>
              <div className="sv17-panel-sub">
                {totalBatches > 0 ? `Lote ${currentBatch} de ${totalBatches}` : 'Consultando el total de registros…'}
              </div>
              <div className="sv17-progress">
                <div className="sv17-progress-bar"><span style={{ width: `${loadingProgress}%` }} /></div>
                <div className="sv17-progress-txt">{loadingProgress}% completado</div>
              </div>
            </div>
          ) : dataFiltrada.length === 0 ? (
            <div className="sv17-panel">
              <div className="sv17-panel-ic"><FiInbox /></div>
              <div className="sv17-panel-title">
                {dataLoaded && dataAgrupada.length === 0 ? 'No hay convocatorias cargadas' : 'Ningún resultado con estos filtros'}
              </div>
              <div className="sv17-panel-sub">
                {dataLoaded && dataAgrupada.length === 0
                  ? 'No hay convocatorias disponibles. Vuelve a intentarlo más tarde.'
                  : 'Prueba a quitar alguna faceta o a ampliar el rango de cierre.'}
              </div>
              {hayFiltros && (
                <div style={{ marginTop: 14 }}>
                  <button className="sv17-btn" onClick={limpiarFiltros}><FiX /> Limpiar filtros</button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="sv17-results">
                {dataPaginada.map((proceso, index) => {
                  const indexGlobal = (currentPage - 1) * itemsPerPage + index;
                  const ci = cierreInfo(proceso);
                  const oc = objetoColor(proceso.objet);
                  const revisado = isProcessReviewed(proceso);

                  return (
                    <article
                      key={`${proceso.nomenclatura}-${indexGlobal}`}
                      className={`sv17-res is-${ci.nivel}`}
                      onClick={() => abrirPanel(indexGlobal)}
                    >
                      <div className="sv17-res-row">
                        <div className="sv17-res-main">
                          <div className="sv17-res-tags">
                            <span className="sv17-code">{proceso.nomenclatura || '—'}</span>
                            {proceso.objet && (
                              <span className="sv17-tag" style={{ color: oc.color, background: oc.bg, borderColor: oc.border }}>
                                <i />
                                {proceso.objet}
                              </span>
                            )}
                            {proceso.tipo_proceso && <span className="sv17-tag-soft">{proceso.tipo_proceso}</span>}
                            {revisado && <span className="sv17-tag-ok"><FiCheck /> Revisada</span>}
                          </div>

                          <div className="sv17-res-desc">{resaltar(proceso.descripcion_proceso)}</div>

                          <div className="sv17-res-meta">
                            <span className="is-strong" title={proceso.entidad}><FiHome />{proceso.entidad || '—'}</span>
                            <span><FiMapPin />{[proceso.departamento, proceso.provincia].filter(Boolean).join(' · ') || '—'}</span>
                            <span><FiList />{proceso.total_items} {proceso.total_items === 1 ? 'ítem' : 'ítems'}</span>
                            <span><FiCalendar />Convocada {fechaCorta(proceso.fecha_convocatoria)}</span>
                          </div>
                        </div>

                        <div className="sv17-res-side">
                          <div className="sv17-cierra">
                            <div className="sv17-cierra-label">CIERRA</div>
                            <div className={`sv17-cierra-val ${ci.nivel === 'urg' ? 'is-urg' : ci.nivel === 'mid' ? 'is-mid' : ''}`}>
                              {ci.texto}
                            </div>
                            {ci.fecha && <div className="sv17-cierra-date">{ci.fecha}</div>}
                          </div>

                          <div className="sv17-res-actions" onClick={(e) => e.stopPropagation()}>
                            <button className="sv17-res-cta" onClick={() => abrirPanel(indexGlobal)}>Ver detalle</button>
                            <button
                              className={`sv17-icobtn ${revisado ? 'is-on' : ''}`}
                              title={revisado ? 'Quitar de revisadas' : 'Marcar como revisada'}
                              onClick={() => toggleProcessReviewed(proceso)}
                            >
                              {revisado ? <FiCheck /> : <FiBookmark />}
                            </button>
                            <BotonDocumentos
                              proceso={proceso}
                              onAbrir={abrirBases}
                              className="sv17-icobtn"
                              titulo="Bases y anexos"
                            />
                            {codigosEnCalendario.has(proceso.nomenclatura) ? (
                              <button
                                className="sv17-icobtn is-added"
                                title="En tu calendario · clic para quitarlo"
                                aria-label="Quitar del calendario"
                                disabled={guardandoCalendario}
                                onClick={() => alternarCalendario(proceso)}
                              >
                                <CalendarCheck size={17} aria-hidden="true" className="sv17-cal-on" />
                                <CalendarX size={17} aria-hidden="true" className="sv17-cal-off" />
                              </button>
                            ) : (
                              <button
                                className="sv17-icobtn"
                                title="Añadir al calendario"
                                aria-label="Añadir al calendario"
                                disabled={guardandoCalendario}
                                onClick={() => alternarCalendario(proceso)}
                              >
                                <FiCalendar />
                              </button>
                            )}
                            <button
                              type="button"
                              className="sv17-icobtn sv17-whatsapp"
                              title="Compartir por WhatsApp"
                              aria-label="Compartir convocatoria por WhatsApp"
                              onClick={() => compartirWhatsApp(proceso)}
                            >
                              <FaWhatsapp aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="sv17-pager">
                <div className="sv17-pager-info">
                  Mostrando <b>{((currentPage - 1) * itemsPerPage + 1).toLocaleString('es-PE')}–
                  {Math.min(currentPage * itemsPerPage, dataFiltrada.length).toLocaleString('es-PE')}</b>{' '}
                  de {dataFiltrada.length.toLocaleString('es-PE')} resultados
                </div>
                <div className="sv17-pager-right">
                  <label className="sv17-perpage">
                    Por página
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); setPanelIndex(null); }}
                    >
                      {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <div className="sv17-pages">
                    <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} title="Anterior">
                      <FiChevronLeft />
                    </button>
                    {paginas.map((p, i) =>
                      p === '…' ? (
                        <span className="sv17-dots" key={`d-${i}`}>…</span>
                      ) : (
                        <button key={p} className={p === currentPage ? 'is-on' : ''} onClick={() => setCurrentPage(p)}>
                          {p}
                        </button>
                      )
                    )}
                    <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} title="Siguiente">
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Modal de detalle (17b) ───────────────────────────── */}
      {procesoPanel && (() => {
        const p = procesoPanel;
        const oc = objetoColor(p.objet);
        const ci = cierreInfo(p);
        const enCurso = registroEnCurso(p);
        const cuenta = cuentaRegresiva(p.fecha_fin_participantes);
        const revisado = isProcessReviewed(p);
        const hayDoc = p.url_doc && p.url_doc.trim() !== '';
        const rnp = capituloRNP(p.objet);
        const deEntidad = dataAgrupada.filter((i) => i.entidad === p.entidad).length;
        const similares = dataAgrupada.filter(
          (i) => i.objet === p.objet && i.departamento === p.departamento && i.nomenclatura !== p.nomenclatura
        ).length;

        const tabs = [
          { id: 'resumen', label: 'Resumen' },
          { id: 'items', label: 'Ítems', count: p.total_items },
          { id: 'cronograma', label: 'Cronograma' },
          { id: 'documentos', label: 'Documentos' }
        ];

        return (
          <div className="sv17-overlay" onClick={cerrarPanel}>
            <div className="sv17-modal" onClick={(e) => e.stopPropagation()}>
              {/* Cabecera con navegación entre resultados */}
              <div className="sv17-modal-top">
                <div className="sv17-modal-pos">
                  Resultado <b>{panelIndex + 1}</b> de {dataFiltrada.length.toLocaleString('es-PE')}
                  {filtros.busqueda.trim() && ` · “${filtros.busqueda.trim()}”`}
                </div>
                <div className="sv17-modal-nav">
                  <button className="sv17-navbtn" onClick={() => panelNav(-1)} disabled={panelIndex <= 0} title="Anterior (K)">
                    <FiChevronLeft /><span>Anterior</span>
                  </button>
                  <button className="sv17-navbtn" onClick={() => panelNav(1)} disabled={panelIndex >= dataFiltrada.length - 1} title="Siguiente (J)">
                    <span>Siguiente</span><FiChevronRight />
                  </button>
                  <button className="sv17-icobtn" onClick={cerrarPanel} title="Cerrar (Esc)"><FiX /></button>
                </div>
              </div>

              {/* Encabezado del proceso */}
              <div className="sv17-modal-hero">
                <div className="sv17-hero-tags">
                  {p.objet && (
                    <span className="sv17-tag" style={{ color: oc.color, background: oc.bg, borderColor: oc.border }}>
                      <i />{p.objet}
                    </span>
                  )}
                  {p.tipo_proceso && <span className="sv17-tag" style={{ background: '#fff', borderColor: '#e2e6ee', color: '#475569' }}>{p.tipo_proceso}</span>}
                  <span className="sv17-tag" style={{ color: '#0f4f47', background: '#e8f2f0', borderColor: '#cfe6dd' }}>
                    <i />{enCurso ? 'Vigente · registro abierto' : 'Vigente'}
                  </span>
                  {ci.dias != null && ci.dias >= 0 && (
                    <span className="sv17-tag" style={{ color: '#8a5308', background: '#fdf3e6', borderColor: '#eddcbf' }}>
                      <FiClock />Cierra {ci.texto.toLowerCase()}
                    </span>
                  )}
                  {revisado && <span className="sv17-tag-ok"><FiCheck /> Revisada</span>}
                </div>

                <h2 className="sv17-hero-title">{p.descripcion_proceso || 'Sin descripción'}</h2>

                <div className="sv17-hero-row">
                  <span className="sv17-hero-code">{p.nomenclatura || '—'}</span>
                  <button
                    className={`sv17-hero-btn ${copiado === 'code' ? 'is-done' : ''}`}
                    onClick={() => copiar(p.nomenclatura || '', 'code')}
                  >
                    {copiado === 'code' ? <><FiCheck /> Copiado</> : <><FiCopy /> Copiar código</>}
                  </button>
                </div>

                <div className="sv17-tabs">
                  {tabs.map((t) => (
                    <button
                      key={t.id}
                      className={modalTab === t.id ? 'is-on' : ''}
                      onClick={() => setModalTab(t.id)}
                    >
                      {t.label}
                      {t.count != null && <b>{t.count}</b>}
                    </button>
                  ))}
                </div>
              </div>

              <div className="sv17-modal-cols">
                {/* Columna principal, según pestaña */}
                <div className="sv17-modal-main">
                  {modalTab === 'resumen' && (
                    <>
                      <div className="sv17-datagrid">
                        <div className="sv17-wide">
                          <label>ENTIDAD CONVOCANTE</label>
                          <strong>{p.entidad || '—'}</strong>
                          <small>{[p.departamento, p.provincia, p.distrito].filter(Boolean).join(' · ') || '—'}</small>
                        </div>
                        <div>
                          <label>OBJETO CONTRACTUAL</label>
                          <span>{p.objet || '—'}</span>
                        </div>
                        <div>
                          <label>MONEDA</label>
                          <span>{p.moneda || '—'}</span>
                        </div>
                        <div>
                          <label>N.º DE PROCESO</label>
                          <span>{p.num_proceso || '—'}</span>
                        </div>
                        <div>
                          <label>ÍTEMS DEL REQUERIMIENTO</label>
                          <span>{p.total_items}</span>
                        </div>
                      </div>

                      {p.items && p.items.length > 0 && (
                        <>
                          <div className="sv17-section">ÍTEMS DEL REQUERIMIENTO<b>{p.total_items}</b></div>
                          <div className="sv17-items">
                            {p.items.slice(0, 3).map((it, i) => (
                              <div className="sv17-item" key={i}>
                                <span className="sv17-item-idx">{i + 1}</span>
                                <div className="sv17-item-body">
                                  <div className="sv17-item-txt">{it.detalle_item || '—'}</div>
                                  <div className="sv17-item-sub">
                                    {it.detCubso && <span>CUBSO {it.detCubso}</span>}
                                    {it.cantItem && <span>· {it.cantItem} {it.detUnidadMedida || 'und'}</span>}
                                  </div>
                                </div>
                              </div>
                            ))}
                            {p.items.length > 3 && (
                              <button className="sv17-link sv17-item-more" onClick={() => setModalTab('items')}>
                                Ver los {p.items.length} ítems
                              </button>
                            )}
                          </div>
                        </>
                      )}

                      <div className="sv17-section">REQUISITOS DEL POSTOR</div>
                      <div className="sv17-reqs">
                        {rnp && <span>Inscripción vigente en el RNP — {rnp}</span>}
                        {p.tipo_proceso && <span>Procedimiento: {p.tipo_proceso}</span>}
                        <span>Los requisitos de calificación se detallan en las bases</span>
                      </div>

                      <div className="sv17-section">DOCUMENTOS</div>
                      <div className="sv17-docs">
                        <button className="sv17-doc" disabled={!hayDoc} onClick={() => abrirBases(p.url_doc)}>
                          <span className="sv17-doc-ic"><FiFileText /></span>
                          <div className="sv17-doc-main">
                            <div className="sv17-doc-title">Bases del procedimiento</div>
                            <div className="sv17-doc-sub">{hayDoc ? 'Se abre en una pestaña nueva' : 'No disponible'}</div>
                          </div>
                        </button>
                        <AnexosConvocatoria
                          texto={p.url_anexos}
                          prefijo="sv17"
                          onAbrir={abrirBases}
                        />
                      </div>
                    </>
                  )}

                  {modalTab === 'items' && (
                    <>
                      <div className="sv17-section">ÍTEMS DEL REQUERIMIENTO<b>{p.total_items}</b></div>
                      {p.items && p.items.length > 0 ? (
                        <div className="sv17-items">
                          {p.items.map((it, i) => (
                            <div className="sv17-item" key={i}>
                              <span className="sv17-item-idx">{i + 1}</span>
                              <div className="sv17-item-body">
                                <div className="sv17-item-txt">{it.detalle_item || '—'}</div>
                                <div className="sv17-item-sub">
                                  {it.detCubso && <span>CUBSO {it.detCubso}</span>}
                                  {it.cantItem && <span>· {it.cantItem} {it.detUnidadMedida || 'und'}</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="sv17-item-more">Esta convocatoria no tiene ítems detallados.</div>
                      )}
                    </>
                  )}

                  {modalTab === 'cronograma' && (
                    <>
                      <div className="sv17-section">CRONOGRAMA COMPLETO</div>
                      <div style={{ marginTop: 9 }}>{renderCrono(p)}</div>
                    </>
                  )}

                  {modalTab === 'documentos' && (
                    <>
                      <div className="sv17-section">DOCUMENTOS</div>
                      <div className="sv17-docs">
                        <button className="sv17-doc" disabled={!hayDoc} onClick={() => abrirBases(p.url_doc)}>
                          <span className="sv17-doc-ic"><FiFileText /></span>
                          <div className="sv17-doc-main">
                            <div className="sv17-doc-title">Bases del procedimiento</div>
                            <div className="sv17-doc-sub">{hayDoc ? 'Se abre en una pestaña nueva' : 'No disponible'}</div>
                          </div>
                        </button>
                        {/* Anexos de la entidad (columna `url_anexos`). */}
                        <AnexosConvocatoria
                          texto={p.url_anexos}
                          prefijo="sv17"
                          onAbrir={abrirBases}
                          conFlecha={false}
                        />
                        <button className="sv17-doc" onClick={() => window.open(SEACE_BUSCADOR_URL, '_blank', 'noopener,noreferrer')}>
                          <span className="sv17-doc-ic is-blue"><FiExternalLink /></span>
                          <div className="sv17-doc-main">
                            <div className="sv17-doc-title">Buscador público del SEACE</div>
                            <div className="sv17-doc-sub">Enlace externo</div>
                          </div>
                        </button>
                        <button className="sv17-doc" onClick={() => copiar(fichaTexto(p), 'doc')}>
                          <span className="sv17-doc-ic is-grey"><FiCopy /></span>
                          <div className="sv17-doc-main">
                            <div className="sv17-doc-title">{copiado === 'doc' ? 'Ficha copiada' : 'Copiar ficha del proceso'}</div>
                            <div className="sv17-doc-sub">Texto listo para pegar</div>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Columna lateral: cronograma, contexto y acciones */}
                <div className="sv17-modal-side">
                  <div className="sv17-crono-head">
                    <span className="sv17-section">CRONOGRAMA</span>
                    {cuenta && (
                      <span className={`sv17-crono-count ${cuenta.urgente ? 'is-urgent' : ''}`}>
                        <FiClock /> {cuenta.label}
                      </span>
                    )}
                  </div>
                  {renderCrono(p)}

                  <div className="sv17-section">CONTEXTO</div>
                  <div className="sv17-ctx">
                    <div>
                      <span>Convocatorias de esta entidad</span>
                      <b>{deEntidad.toLocaleString('es-PE')}</b>
                    </div>
                    <div>
                      <span>Procesos similares vigentes</span>
                      {similares > 0 ? (
                        <button
                          className="sv17-link"
                          onClick={() => {
                            setFiltros({
                              ...FILTROS_VACIOS,
                              objetos: p.objet ? [p.objet] : [],
                              departamentos: p.departamento ? [p.departamento] : []
                            });
                            setCurrentPage(1);
                            setPanelIndex(null);
                          }}
                        >
                          Ver {similares.toLocaleString('es-PE')}
                        </button>
                      ) : (
                        <b>0</b>
                      )}
                    </div>
                  </div>

                  <div className="sv17-actions">
                    {codigosEnCalendario.has(p.nomenclatura) ? (
                      <button
                        className="sv17-action is-added"
                        onClick={() => alternarCalendario(p)}
                        disabled={guardandoCalendario}
                      >
                        <CalendarCheck size={16} aria-hidden="true" />
                        <span className="sv17-action-txt">
                          <b>Ya está en tu calendario</b>
                          <small>Clic para quitarlo</small>
                        </span>
                      </button>
                    ) : (
                      <button className="sv17-action" onClick={() => alternarCalendario(p)} disabled={guardandoCalendario}>
                        <FiCalendar />
                        <span className="sv17-action-txt">
                          <b>Añadir al calendario</b>
                          <small>Crea recordatorios de los hitos</small>
                        </span>
                      </button>
                    )}
                    <button className="sv17-action" onClick={() => generarOferta(p)}>
                      <FiFilePlus className="is-blue" />
                      <span className="sv17-action-txt">
                        <b>Generar oferta</b>
                        <small>Abre el asistente con estos datos</small>
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Pie con atajos y acciones principales */}
              <div className="sv17-modal-foot">
                <span className="sv17-kbd">
                  <FiCornerDownRight />
                  <span><b>J / K</b> recorre resultados · <b>S</b> marca revisada · <b>Esc</b> cierra</span>
                </span>
                <div className="sv17-foot-actions">
                  <button
                    className={`sv17-btn ${revisado ? 'sv17-btn-tint' : ''}`}
                    onClick={() => toggleProcessReviewed(p)}
                  >
                    {revisado ? <><FiCheck /> Revisada</> : <><FiBookmark /> Marcar como revisada</>}
                  </button>
                  <button type="button" className="sv17-btn sv17-modal-whatsapp" onClick={() => compartirWhatsApp(p)}>
                    <FaWhatsapp aria-hidden="true" /> Compartir por WhatsApp
                  </button>
                  <button type="button" className="sv17-btn sv17-btn-primary" disabled={!hayDoc} onClick={() => abrirBases(p.url_doc)}>
                    <FiExternalLink /> Ver Bases
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default SEACE;
